import { Grid } from '@mui/material';
import { useState } from 'react';
import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import Eyebrow from '@/components/Eyebrow/Eyebrow';
import HighlightLightboxVideoPlayer from '@/components/HighlightLightboxPlayer/HighlightLightboxVideoPlayer';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import type {
  Contentful,
  FrontifyImageContent,
} from '@/contentful/contentful-exports';
import useGlobalStyles from '@/styles/global.styles';
import type { TypeHighlightLightbox } from '@/types/contentful/TypeHighlightLightbox';
import Heading from '../Heading/Heading';
import useStyles from './HighlightLightbox.styles';
import HighlightLightboxImage from './HighlightLightboxImage';

type HighlightLightboxFields = Omit<
  TypeHighlightLightbox<'WITHOUT_UNRESOLVABLE_LINKS'>['fields'],
  'thumbnail'
> & {
  thumbnail: [FrontifyImageContent];
};

export interface HighlightLightboxContent extends Contentful {
  fields: HighlightLightboxFields;
}

const HighlightLightbox = ({
  content,
}: {
  content: HighlightLightboxContent;
}) => {
  const {
    eyebrow,
    style,
    playButtonStyle,
    thumbnail,
    textAlignment,
    headline,
    subheading,
    cta,
    ctaRef,
    videoLink,
    backButtonText,
    mobileOrientation,
  } = content.fields;
  const ctaLabel = ctaRef?.fields.text ?? cta;
  const ctaHref = ctaRef?.fields.url;
  const videoUrl = ctaRef?.fields.videoUrl ?? videoLink;
  const hasVideo = Boolean(videoUrl);
  const hasLink = Boolean(ctaHref);
  const isOnDark = style === 'OnDark';
  const isPlayButtonOnDark = playButtonStyle === 'OnDark';
  const isRightAligned = textAlignment === 'Right';
  const { classes, cx } = useStyles({
    isOnDark,
    isRightAligned,
    isPlayButtonOnDark,
    mobileOrientation,
  });
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);

  const togglePlayer = () => setIsPlayerOpen((prev) => !prev);

  return (
    <section className={classes.container}>
      <GridContainer
        fixed
        gridProps={{
          className: classes.content,
          rowSpacing: { xs: 2, sm: 3, lg: 4 },
        }}
      >
        <Grid size={{ xs: 12, md: 6 }}>
          <HighlightLightboxImage
            thumbnail={thumbnail}
            hasVideo={hasVideo}
            href={hasLink ? ctaHref : undefined}
            isOnDark={isOnDark}
            isPlayButtonOnDark={isPlayButtonOnDark}
            isRightAligned={isRightAligned}
            mobileOrientation={mobileOrientation}
            onVideoOpen={togglePlayer}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <div className={classes.textContent}>
            <div className={classes.headingGroup}>
              {eyebrow && <Eyebrow text={eyebrow} isOnDark={isOnDark} />}
              <Heading level='h2' className={classes.heading}>
                {headline}
              </Heading>
            </div>
            {subheading && (
              <p className={cx(globalClasses.bodyLg, classes.subheading)}>
                {subheading}
              </p>
            )}
            {ctaLabel && (
              <Button
                className={classes.cta}
                label={ctaLabel}
                variant={ButtonVariant.SECONDARY}
                onClick={hasVideo ? togglePlayer : undefined}
                href={!hasVideo && hasLink ? ctaHref : undefined}
                isOnDark={isOnDark}
                size={{
                  xs: ButtonSize.SM,
                  sm: ButtonSize.MD,
                  lg: ButtonSize.LG,
                }}
              />
            )}
          </div>
        </Grid>
      </GridContainer>

      {videoUrl && (
        <HighlightLightboxVideoPlayer
          videoLink={videoUrl}
          isOpen={isPlayerOpen}
          backButtonText={backButtonText}
          onClose={() => setIsPlayerOpen(false)}
        />
      )}
    </section>
  );
};

export default HighlightLightbox;
